// Read-only flowchart viewer used by the Detail screens.
// Web: renders React Flow in a non-interactive configuration so the
// user can still pan/zoom/look at the minimap. Native: falls back to
// a static SVG rendering that's good enough for the rare mobile
// glance and saves us the React-Flow-on-native problem entirely.

import React from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import { COLORS, FONTS } from '../../constants/config';
import {
  EMPTY_FLOWCHART,
  NODE_VISUAL_PRESETS,
  type FlowchartData,
} from './types';

interface Props {
  value: FlowchartData | null | undefined;
  height?: number;
  emptyMessage?: string;
}

export default function FlowchartViewer({ value, height = 360, emptyMessage }: Props) {
  const isEmpty = !value || !value.nodes || value.nodes.length === 0;

  if (isEmpty) {
    return (
      <View style={[styles.emptyState, { height }]}>
        <Text style={styles.emptyText}>
          {emptyMessage ?? 'لم يُرسم مخطط تدفق لهذا العنصر بعد.'}
        </Text>
      </View>
    );
  }

  if (Platform.OS === 'web') {
    // Load the React Flow renderer lazily and ONLY on web. It lives in its
    // own module that imports @xyflow/react via ESM — the same instance
    // ./nodes and FlowchartDesigner use — so the custom-node Handles and the
    // ReactFlowProvider share one store context (see FlowchartWebViewer for
    // the dual-package-hazard / error-#001 explanation). require() here keeps
    // the native bundler from executing those DOM-only internals.
    const WebViewer = require('./FlowchartWebViewer').default;
    return <WebViewer value={value!} height={height} />;
  }
  return <NativeViewer value={value!} height={height} />;
}

// ────────────────────────────────────────────────────────────────────
// Native: static SVG. We measure the bounding box of all nodes,
// translate everything into a viewBox, and render shapes + arrows.
// No layout magic — we just trust the positions the designer saved.
// ────────────────────────────────────────────────────────────────────
function NativeViewer({ value, height }: { value: FlowchartData; height: number }) {
  const { Svg, Rect, Polygon, Circle, Path, Text: SvgText, G } = require('react-native-svg');

  const padding = 40;
  const xs = value.nodes.map((n) => n.position.x);
  const ys = value.nodes.map((n) => n.position.y);
  const widths = value.nodes.map((n) => NODE_VISUAL_PRESETS[n.type].width);
  const heights = value.nodes.map((n) => NODE_VISUAL_PRESETS[n.type].height);

  const minX = Math.min(...xs) - padding;
  const minY = Math.min(...ys) - padding;
  const maxX = Math.max(...xs.map((x, i) => x + widths[i])) + padding;
  const maxY = Math.max(...ys.map((y, i) => y + heights[i])) + padding;
  const vbWidth = Math.max(maxX - minX, 100);
  const vbHeight = Math.max(maxY - minY, 100);

  // Helper to find a node's centre — used for connecting edges.
  const nodeById = Object.fromEntries(value.nodes.map((n) => [n.id, n]));
  function centre(nodeId: string): { x: number; y: number } | null {
    const node = nodeById[nodeId];
    if (!node) return null;
    const preset = NODE_VISUAL_PRESETS[node.type];
    return {
      x: node.position.x + preset.width / 2,
      y: node.position.y + preset.height / 2,
    };
  }

  return (
    <View style={{ height, borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#FAFAFA' }}>
      <Svg
        width="100%"
        height={height}
        viewBox={`${minX} ${minY} ${vbWidth} ${vbHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Edges first so nodes paint on top */}
        {value.edges.map((edge) => {
          const a = centre(edge.source);
          const b = centre(edge.target);
          if (!a || !b) return null;
          return (
            <G key={edge.id}>
              <Path
                d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`}
                stroke="#94A3B8"
                strokeWidth={1.5}
                fill="none"
              />
              {edge.label && (
                <SvgText
                  x={(a.x + b.x) / 2}
                  y={(a.y + b.y) / 2 - 6}
                  fontSize={11}
                  fill="#475569"
                  textAnchor="middle"
                >
                  {edge.label}
                </SvgText>
              )}
            </G>
          );
        })}

        {/* Nodes */}
        {value.nodes.map((node) => {
          const preset = NODE_VISUAL_PRESETS[node.type];
          const cx = node.position.x + preset.width / 2;
          const cy = node.position.y + preset.height / 2;
          return (
            <G key={node.id}>
              {preset.shape === 'pill' && (
                <Rect
                  x={node.position.x}
                  y={node.position.y}
                  width={preset.width}
                  height={preset.height}
                  rx={preset.height / 2}
                  ry={preset.height / 2}
                  fill={preset.bg}
                  stroke={preset.border}
                  strokeWidth={2}
                />
              )}
              {preset.shape === 'rect' && (
                <Rect
                  x={node.position.x}
                  y={node.position.y}
                  width={preset.width}
                  height={preset.height}
                  rx={8}
                  ry={8}
                  fill={preset.bg}
                  stroke={preset.border}
                  strokeWidth={2}
                />
              )}
              {preset.shape === 'diamond' && (
                <Polygon
                  points={[
                    `${cx},${node.position.y}`,
                    `${node.position.x + preset.width},${cy}`,
                    `${cx},${node.position.y + preset.height}`,
                    `${node.position.x},${cy}`,
                  ].join(' ')}
                  fill={preset.bg}
                  stroke={preset.border}
                  strokeWidth={2}
                />
              )}
              <SvgText
                x={cx}
                y={cy + 4}
                fontSize={12}
                fontWeight="bold"
                fill={preset.text}
                textAnchor="middle"
              >
                {(node.label || '').slice(0, 24)}
              </SvgText>
            </G>
          );
        })}
      </Svg>
    </View>
  );
}

const styles = StyleSheet.create({
  emptyState: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#CBD5E1',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    padding: 24,
  },
  emptyText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
  },
});

// Re-export EMPTY_FLOWCHART for screens that want to seed a fresh
// diagram when the user opens an Add screen.
export { EMPTY_FLOWCHART } from './types';
